<template>
  <div class="point-sheet" role="dialog" :aria-label="$t('map.pointTool.title')">
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
        :aria-label="$t('map.pointTool.close')"
        @click="$emit('close')"
      >
        <ArrowLeft class="w-4 h-4" />
      </button>
      <div class="min-w-0 flex-1">
        <p class="text-[13px] font-semibold leading-tight truncate">
          {{ marker ? marker.name : $t('map.pointTool.title') }}
        </p>
        <p class="text-[11.5px] text-[hsl(var(--muted-foreground))] tabular-nums">{{ coordsText }}</p>
      </div>
      <button
        type="button"
        class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
        :aria-label="$t('map.pointTool.copy')"
        :title="copied ? $t('map.pointTool.copied') : $t('map.pointTool.copy')"
        @click="copyCoords"
      >
        <Check v-if="copied" class="w-4 h-4 text-[hsl(var(--primary))]" />
        <Copy v-else class="w-4 h-4" />
      </button>
    </div>

    <!-- Назвать метку: своё имя или стандартный тип. -->
    <form v-if="naming" class="mt-3 flex flex-col gap-2" @submit.prevent="submitName">
      <input
        v-model="name"
        type="text"
        maxlength="100"
        class="h-9 px-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[13px]"
        :placeholder="$t('map.pointTool.namePlaceholder')"
        :aria-label="$t('map.pointTool.namePlaceholder')"
      />
      <div class="flex flex-wrap gap-1.5" role="group" :aria-label="$t('map.pointTool.kindLabel')">
        <button
          v-for="k in STANDARD_KINDS"
          :key="k"
          type="button"
          class="h-7 px-2.5 rounded-full border text-[11.5px] font-semibold transition-colors"
          :class="kind === k
            ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))]'
            : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]'"
          @click="pickKind(k)"
        >{{ $t(`map.pointTool.kind.${k}`) }}</button>
      </div>
      <div class="flex gap-2">
        <button
          type="submit"
          :disabled="!name.trim() || busy"
          class="flex-1 h-9 rounded-full bg-[hsl(var(--primary))] text-white text-[12.5px] font-semibold disabled:opacity-50"
        >{{ $t('map.pointTool.saveMarker') }}</button>
        <button
          type="button"
          class="h-9 px-4 rounded-full border border-[hsl(var(--border))] text-[12.5px] font-semibold"
          @click="naming = false"
        >{{ $t('map.pointTool.cancel') }}</button>
      </div>
    </form>

    <div v-else class="mt-3 grid grid-cols-4 gap-1.5">
      <!-- Своя метка: у сохранённой - переименовать, у новой - назвать. -->
      <button v-if="authed" type="button" class="point-sheet__action" @click="startNaming">
        <MapPin class="w-[18px] h-[18px]" />
        <span>{{ marker ? $t('map.pointTool.rename') : $t('map.pointTool.ownMarker') }}</span>
      </button>
      <button v-else type="button" class="point-sheet__action opacity-60" :title="$t('map.pointTool.loginRequired')" @click="$emit('login')">
        <MapPin class="w-[18px] h-[18px]" />
        <span>{{ $t('map.pointTool.ownMarker') }}</span>
      </button>
      <button type="button" class="point-sheet__action" @click="$emit('routes-here')">
        <Route class="w-[18px] h-[18px]" />
        <span>{{ $t('map.pointTool.routesHere') }}</span>
      </button>
      <button type="button" class="point-sheet__action" @click="$emit('start-here')">
        <Flag class="w-[18px] h-[18px]" />
        <span>{{ $t('map.pointTool.startHere') }}</span>
      </button>
      <button v-if="marker" type="button" class="point-sheet__action text-[hsl(var(--danger))]" :disabled="busy" @click="$emit('delete')">
        <Trash2 class="w-[18px] h-[18px]" />
        <span>{{ $t('map.pointTool.delete') }}</span>
      </button>
      <button v-else type="button" class="point-sheet__action" @click="share">
        <Share2 class="w-[18px] h-[18px]" />
        <span>{{ $t('map.pointTool.share') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ArrowLeft, Check, Copy, Flag, MapPin, Route, Share2, Trash2 } from "lucide-vue-next";
import type { MarkerKind, UserMarker } from "../store/myMarkers";

const STANDARD_KINDS: MarkerKind[] = ["camp", "water", "viewpoint", "danger", "parking", "other"];

const props = defineProps<{
  point: { lng: number; lat: number };
  marker?: UserMarker | null;
  authed: boolean;
  busy?: boolean;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "login"): void;
  (e: "routes-here"): void;
  (e: "start-here"): void;
  (e: "delete"): void;
  (e: "save", payload: { name: string; kind: MarkerKind }): void;
}>();

const { t } = useI18n();

const coordsText = computed(() => `${props.point.lat.toFixed(5)}, ${props.point.lng.toFixed(5)}`);

const naming = ref(false);
const name = ref("");
const kind = ref<MarkerKind>("custom");
const copied = ref(false);

// Сменилась точка/метка - форма названия от прошлой не должна переезжать.
watch(
  () => [props.point.lat, props.point.lng, props.marker?.id],
  () => {
    naming.value = false;
    copied.value = false;
  }
);

const startNaming = () => {
  name.value = props.marker?.name ?? "";
  kind.value = props.marker?.kind ?? "custom";
  naming.value = true;
};

// Тип подставляет имя только в пустое поле - не затирает то, что человек уже
// набрал руками.
const pickKind = (k: MarkerKind) => {
  kind.value = k;
  if (!name.value.trim()) name.value = t(`map.pointTool.kind.${k}`);
};

const submitName = () => {
  const trimmed = name.value.trim();
  if (!trimmed) return;
  emit("save", { name: trimmed, kind: kind.value });
  naming.value = false;
};

const writeClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const copyCoords = async () => {
  if (await writeClipboard(coordsText.value)) {
    copied.value = true;
    window.setTimeout(() => (copied.value = false), 1500);
  }
};

const share = async () => {
  const text = coordsText.value;
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ title: t("map.pointTool.title"), text });
      return;
    } catch {
      /* отмена шаринга - не ошибка; ниже без повторов */
      return;
    }
  }
  await copyCoords();
};
</script>

<style scoped>
.point-sheet {
  padding: 12px;
  border-radius: 14px;
  background: var(--card-hex);
  border: 1px solid hsl(var(--border) / 0.6);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.12);
  color: hsl(var(--foreground));
}
.point-sheet__action {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 2px;
  border-radius: 10px;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  color: hsl(var(--foreground) / 0.85);
  transition: background-color 0.15s;
}
.point-sheet__action:hover {
  background: hsl(var(--muted));
}
.point-sheet__action:disabled {
  opacity: 0.5;
}
</style>
